// Editor environment configuration (worker API, dashboard origin, public site domain).
export const WORKER_URL = (import.meta.env.VITE_WORKER_URL as string | undefined) ?? 'http://localhost:8787'
/**
 * El origen del dashboard. En local llega por `VITE_DASHBOARD_URL`; en el build
 * de producción esa variable no está definida y el fallback a `localhost` dejaba
 * sin salida a las pantallas que devuelven al dashboard (login, handoff, errores
 * de proyecto), así que antes de rendirse se mira `VITE_APP_URL` y, si tampoco,
 * el propio dominio.
 */
export const DASHBOARD_URL = (import.meta.env.VITE_DASHBOARD_URL as string | undefined)
  ?? (import.meta.env.VITE_APP_URL as string | undefined)
  // `typeof window`: este módulo también se carga en tests, que corren en Node.
  ?? (typeof window !== 'undefined' && window.location.hostname.endsWith('veltica.com')
    ? 'https://app.veltica.com'
    : 'http://localhost:5174')
/**
 * La clave de Google para los mapas (Maps Embed API). Va dentro del iframe de
 * cada mapa publicado, así que es pública por diseño: en Google Cloud se limita
 * a «Maps Embed API» y nada más, que es gratis y sin límite de uso. No servir
 * con ella ninguna otra API de pago (Places, Geocoding…): ésas van con otra
 * clave, secreta, en el worker. Vacía = el componente de mapa no se ofrece.
 */
export const GOOGLE_MAPS_EMBED_KEY = (import.meta.env.VITE_GOOGLE_MAPS_EMBED_KEY as string | undefined)?.trim() ?? ''
export const PUBLIC_SITE_DOMAIN = (import.meta.env.VITE_PUBLIC_SITE_DOMAIN as string | undefined) ?? 'veltica.com'

/**
 * La dirección de un sitio publicado en su subdominio.
 *
 * El `https://` iba fijo en cada sitio que arma esta URL, así que trabajando en
 * local «Ver sitio» salía a internet: publicabas contra la base de Docker y el
 * enlace abría el Worker de producción, que no conoce ese proyecto. En local el
 * sitio vive en `algo.localhost:8787` y habla http.
 */
export function publicSiteUrl(subdomain: string): string {
  const scheme = PUBLIC_SITE_DOMAIN.split(':')[0] === 'localhost' ? 'http' : 'https'
  return `${scheme}://${subdomain}.${PUBLIC_SITE_DOMAIN}`
}

export const HELP_SITE = (import.meta.env.VITE_HELP_URL as string | undefined)
  ?? (typeof window !== 'undefined' && window.location.hostname.endsWith('veltica.com')
    ? 'https://help.veltica.com'
    : 'http://localhost:5175')

/* Cada panel del editor con su artículo: direcciones REALES de la ayuda
   (`<tema>/<archivo>`, iguales en inglés). `appConfig.test.ts` comprueba que
   existan: un enlace de ayuda que acaba en 404 es peor que ninguno. */
export const EDITOR_PANEL_HELP_PATHS: Record<string, string> = {
  insert: 'editor/insertar-elementos-y-bloques',
  templates: 'editor/secciones-predisenadas-y-disenos-de-pagina',
  content: 'ventas-y-cobros/mostrar-catalogo-y-activar-carrito',
  catalog: 'ventas-y-cobros/mostrar-catalogo-y-activar-carrito',
  blog: 'paginas-y-cms/categorias-autores-y-diseno-del-blog',
  styles: 'editor/estilo-de-marca-logo-colores-y-tipografias',
  images: 'editor/imagenes-videos-y-archivos',
  files: 'editor/imagenes-videos-y-archivos',
  layers: 'editor/inspector-avanzado-y-capas',
  pages: 'paginas-y-cms/administracion-de-paginas',
  'single-pages': 'paginas-y-cms/administracion-de-paginas',
  cms: 'paginas-y-cms/conectar-cms-al-editor-y-paginas-dinamicas',
}

export const EDITOR_DEFAULT_HELP_PATH = 'editor/modo-esencial-y-opciones-avanzadas'

export function editorHelpUrl(language: 'es' | 'en' = 'es', panel?: string | null): string {
  const base = HELP_SITE.replace(/\/+$/, '')
  const prefix = language === 'en' ? '/en' : ''
  const path = (panel && EDITOR_PANEL_HELP_PATHS[panel]) || EDITOR_DEFAULT_HELP_PATH
  return `${base}${prefix}/${path}`
}

